import StandardTeaserComponents from '@components/Teaser/Standard'
import config from '@config'
import contextLayoutMultiLine from '@contexts/layoutMultiLine'
import contextMegaModule from '@contexts/megaModule'
import contextStandardTeaser from '@contexts/standardTeaser'
import useAureusImpressionTracking from '@hooks/useAureusImpressionTracking'
import useTracking, { TrackingFnType } from '@hooks/useTracking'
import { stripHtml } from '@hooks/useTracking/utils'
import useViewportTracking from '@hooks/useViewportTracking'
import { isVideoOnlyArticle } from '@widgets/Teaser/utils'
import { CookWidget } from '@widgets/types'
import { Fragment, FunctionComponent, useCallback, useContext } from 'react'
import { useTheme } from 'styled-components'
import SportsHeader from '../sportsHeader'
import { TeaserFlexValueDesktop } from '../types'
import ExtraData from './ExtraData'
import StandardTeaserLayoutContextProvider from './StandardTeaserLayoutContextProvider'
import TeaserImage from './TeaserImage'
import { StandardTeaserAPIProps } from './types'

type StandardTeaserProps = StandardTeaserAPIProps

const {
  backend: { baseUrl },
} = config

const {
  Catchword,
  EverythingWrapper,
  ExtraDataWrapper,
  ImageOverlayWrapper,
  ImageWrapper,
  Label,
  OuterWrapper,
  PromoIndicator,
  PromoLabel,
  PromoLabelLogoWrapper,
  TextWrapper,
  Title,
  VideoInfoBig,
} = StandardTeaserComponents

const { LayoutMultiLineContext } = contextLayoutMultiLine
const { MegaModuleContext } = contextMegaModule
const { StandardTeaserLayoutContext } = contextStandardTeaser

const StandardTeaser: FunctionComponent<StandardTeaserProps> = (props) => {
  const theme = useTheme()
  const {
    articleId,
    image = {
      src: '/incoming/31574-v2-missing.png',
      alt: 'image not found',
    },
    accentColor = theme.colors.brandColors.brand_55,
    articleType = 'default',
    label,
    targetContentType,
    video,
    track,
    recommendationData,
    title,
    catchword,
    logo,
    parentProps,
    promo,
    matchId,
    typeOfSport,
    link,
  } = props

  const layoutMultilineContext = useContext(LayoutMultiLineContext)
  const megaModuleContext = useContext(MegaModuleContext)

  const teaserFlex = parentProps?.flex || '2'
  const hasExtraLargeTabletStyle =
    !!megaModuleContext &&
    megaModuleContext.size === 4 &&
    parentProps?.positionInMegaModule === 1
  const isAncestorVideoBlock = !!layoutMultilineContext?.isVideoBlock
  const isVideoOnly = isVideoOnlyArticle(targetContentType)
  const { src: imageSrc, alt: imageAlt, crops: imageAvailableCrops } = image
  const hasPromo = promo?.isPromo
  const cleanedUpMatchId = matchId?.startsWith('stage')
    ? matchId.substring('stage'.length)
    : matchId
  const hasMatch = cleanedUpMatchId && typeOfSport
  const aureusOfferId = recommendationData?.aureusOfferId

  const onImpression = useCallback<TrackingFnType>(() => {
    if (recommendationData) {
      return {
        event: 'recommended_teaser_impression',
        eventCategory:
          targetContentType === 'video'
            ? 'recommended_video'
            : 'recommended_article',
        eventLabel: `${articleId}:${stripHtml(title)}`,
        eventAction: `impression_${recommendationData.position}`,
        id: articleId,
        correlationId: recommendationData.correlationId,
        recoArticleIds: recommendationData.articleIds,
        recommendedPosition: recommendationData.position,
        targetContentType,
      }
    } else {
      return {
        event: 'teaser_impression',
        eventCategory: video ? 'video_teaser' : 'article_teaser',
        eventLabel: `${articleId}:standard:${stripHtml(title)}`,
        eventAction: 'impression',
        id: articleId,
      }
    }
  }, [articleId, recommendationData, targetContentType, title, video])

  const trackedOnImpression = useTracking(onImpression)

  const onPromoImpression = useCallback(
    () => ({
      event: 'contentcommerce_impression',
      eventCategory: 'BrandStudio',
      eventValue: 1,
      eventAction: 'Impression / Brandstudio',
      nonInteraction: true,
      impressionUrl: promo?.tradeDoublerUrl ?? '',
    }),
    [promo?.tradeDoublerUrl]
  )

  const trackedOnPromoImpression = useTracking(onPromoImpression)

  const triggerAureusImpression = useAureusImpressionTracking()

  const finalOnImpression = useCallback(() => {
    triggerAureusImpression({
      aureusOfferId,
      isRecommendation: !!recommendationData,
    })
    if (!!track) {
      trackedOnImpression()
      if (promo?.isPromo) {
        trackedOnPromoImpression()
      }
    }
  }, [
    aureusOfferId,
    promo?.isPromo,
    recommendationData,
    track,
    trackedOnImpression,
    trackedOnPromoImpression,
    triggerAureusImpression,
  ])

  const viewportRef = useViewportTracking({
    onImpression: finalOnImpression,
    track: true,
  })

  const onClick = useCallback(() => {
    if (recommendationData) {
      return {
        event: 'click_moreContent',
        eventCategory: 'related content',
        eventLabel: `${baseUrl}/id${articleId}.html`,
        eventAction:
          targetContentType === 'video'
            ? 'click_moreContent_video'
            : 'click_moreContent_article',
        id: articleId,
        correlationId: recommendationData.correlationId,
        recoArticleIds: recommendationData.articleIds,
        targetContentType,
        recommendedPosition: recommendationData.position,
      }
    } else {
      return {
        event: 'teaser_click',
        eventCategory: video ? 'video_teaser' : 'article_teaser',
        eventLabel: `${articleId}:standard:${stripHtml(title)}`,
        eventAction: 'click',
        id: articleId,
      }
    }
  }, [articleId, recommendationData, targetContentType, title, video])

  const trackedOnClick = useTracking(onClick)

  const clickableProps = {
    aureusOfferId,
    ...(track ? { onClick: trackedOnClick } : {}),
    ...link,
  }

  return (
    <Fragment key={articleId}>
      <StandardTeaserLayoutContextProvider
        teaserFlex={teaserFlex[0] as TeaserFlexValueDesktop}
        accentColor={megaModuleContext?.accentColor ?? accentColor}
        isInMegaModule={!!megaModuleContext}
        hasExtraLargeTabletStyle={hasExtraLargeTabletStyle}
        hasMiniDesktopStyle={megaModuleContext?.size === 4}
        isVideoOnly={isVideoOnly}
        articleType={articleType}
        isPromo={!!promo?.isPromo}
        hasVideoOnlyStyle={isVideoOnly || isAncestorVideoBlock}>
        <EverythingWrapper
          viewportRef={viewportRef}
          clickableProps={
            clickableProps as unknown as { href: string; target: any } & Record<
              string,
              unknown
            >
          }>
          <OuterWrapper>
            <ImageWrapper>
              <TeaserImage
                src={imageSrc}
                alt={imageAlt}
                availableCrops={imageAvailableCrops}
              />
              <ImageOverlayWrapper>
                {cleanedUpMatchId && typeOfSport ? (
                  <StandardTeaserLayoutContext.Consumer>
                    {({ teaserFlex }) => (
                      <SportsHeader
                        matchId={cleanedUpMatchId}
                        typeOfSport={typeOfSport}
                        teaserFlex={{
                          desktop: teaserFlex,
                          tablet: '3',
                          mobile: '6',
                        }}
                        teaserType="standard"
                      />
                    )}
                  </StandardTeaserLayoutContext.Consumer>
                ) : null}
                {!hasMatch && !hasPromo ? (
                  <ExtraDataWrapper>
                    <ExtraData isOnImage={true} video={video} />
                  </ExtraDataWrapper>
                ) : null}

                {promo?.isPromo ? (
                  <PromoIndicator>
                    <PromoLabel isOnImage={true}>{promo.promoLabel}</PromoLabel>
                    <ExtraDataWrapper>
                      <ExtraData isOnImage={true} logo={logo} video={video} />
                    </ExtraDataWrapper>
                  </PromoIndicator>
                ) : null}
                {isVideoOnly ? <VideoInfoBig /> : null}
                {label ? <Label isOnImage={true}>{label.text}</Label> : null}
              </ImageOverlayWrapper>
            </ImageWrapper>
            <TextWrapper>
              {promo?.isPromo ? (
                <PromoLabelLogoWrapper
                  isOnImage={false}
                  labelLength={promo.promoLabel.length}>
                  <PromoLabel isOnImage={false}>{promo.promoLabel}</PromoLabel>
                  <ExtraData isOnImage={false} logo={logo} />
                </PromoLabelLogoWrapper>
              ) : null}
              {label ? <Label isOnImage={false}>{label.text}</Label> : null}
              {catchword ? <Catchword>{catchword}</Catchword> : null}
              <Title>{title}</Title>
            </TextWrapper>
          </OuterWrapper>
        </EverythingWrapper>
      </StandardTeaserLayoutContextProvider>
    </Fragment>
  )
}

const widget: CookWidget = {
  kind: ['widget', 'teaser', 'standard'],
  component: StandardTeaser,
}

export default widget
